import { Children, isValidElement, type ReactNode } from "react";
import {
	AGENT_ACCENTS,
	AGENT_ICONS,
	type AgentKey,
} from "@/components/brand/ai-agent-icons";
import { FeatureGrid } from "@/components/feature-grid";

function shouldAutoFlush(children: ReactNode): boolean {
	const arr = Children.toArray(children);
	if (arr.length !== 1) return false;
	const only = arr[0];
	return isValidElement(only) && only.type === FeatureGrid;
}

export function MarketingSection({
	id,
	eyebrow,
	agent,
	title,
	description,
	children,
	align = "center",
	flush,
}: {
	id?: string;
	eyebrow?: string;
	agent?: AgentKey;
	title?: string;
	description?: string;
	children: ReactNode;
	align?: "center" | "left";
	flush?: boolean;
}) {
	const headingAlign = align === "center" ? "text-center" : "text-left";
	const itemsAlign = align === "center" ? "items-center" : "items-start";
	const hasHeading = !!(eyebrow || title || description || agent);
	const isFlush = flush ?? shouldAutoFlush(children);
	const childPaddingTop = hasHeading ? "" : "pt-12 sm:pt-14";
	const childPaddingBottom = isFlush ? "" : "pb-12 sm:pb-14";
	const childPadding = `${childPaddingTop} ${childPaddingBottom}`.trim();

	return (
		<section
			id={id}
			className="rounded-md bg-white dark:bg-black scroll-mt-24"
		>
			{hasHeading && (
				<div
					className={`mx-auto flex max-w-2xl flex-col ${itemsAlign} py-12 sm:py-14 ${headingAlign}`}
				>
					{agent && (
						<span
							className={`mb-4 flex size-12 items-center justify-center rounded-full ${AGENT_ACCENTS[agent].bg}`}
						>
							{AGENT_ICONS[agent](`size-7 ${AGENT_ACCENTS[agent].text}`)}
						</span>
					)}
					{eyebrow && (
						<p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
							{eyebrow}
						</p>
					)}
					{title && (
						<h2
							className={`text-2xl font-semibold tracking-[-0.03em] sm:text-3xl ${eyebrow ? "mt-2" : ""}`}
						>
							{title}
						</h2>
					)}
					{description && (
						<p className="mt-3 text-sm text-muted-foreground sm:text-base">
							{description}
						</p>
					)}
				</div>
			)}
			{childPadding ? <div className={childPadding}>{children}</div> : children}
		</section>
	);
}
